import type { ErrorEnvelope, SuccessEnvelope } from "@whitehaven/contracts";
import type {
  HomepageBackupExport,
  HomepageContent,
  HomepageContentState,
  HomepageRevisionDetail,
  HomepageRevisionHistory,
} from "@whitehaven/contracts/homepage";
import type {
  GoogleTagManagerConfirmation,
  GoogleTagManagerSettingsState,
} from "@whitehaven/contracts/google-tag-manager";
import type {
  AuthorizationSubject,
  Permission,
  Role,
} from "@whitehaven/permissions";
import { permissions, roles } from "@whitehaven/permissions";

export interface PortalSessionData {
  readonly subject: AuthorizationSubject;
  readonly organizationId: string;
  readonly csrfToken: string;
  readonly csrfExpiresAt: string;
  readonly idleExpiresAt: string;
  readonly absoluteExpiresAt: string;
}

export interface HomepageHistoryOptions {
  readonly limit?: number;
  readonly beforeRevisionNumber?: number;
}

export type HomepageEditorFetch = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;

export class HomepageEditorApiError extends Error {
  readonly status: number;
  readonly codes: readonly string[];
  readonly correlationId: string | null;

  constructor(
    message: string,
    status: number,
    codes: readonly string[] = [],
    correlationId: string | null = null,
  ) {
    super(message);
    this.name = "HomepageEditorApiError";
    this.status = status;
    this.codes = codes;
    this.correlationId = correlationId;
  }

  get isAuthenticationRequired(): boolean {
    return this.status === 401;
  }

  get isAccessDenied(): boolean {
    return this.status === 403;
  }

  get isConflict(): boolean {
    return (
      this.status === 409 &&
      this.codes.some((code) =>
        [
          "CONTENT_CONFLICT",
          "CONTENT_AUTHORIZATION_CHANGED",
          "CONTENT_CONFIRMATION_REQUIRED",
          "GTM_CONFLICT",
          "GTM_CONFIRMATION_REQUIRED",
        ].includes(code),
      )
    );
  }
}

export interface HomepageEditorApiClient {
  readonly apiUrl: string;
  loadSession(signal?: AbortSignal): Promise<PortalSessionData>;
  exchangeFirebaseSession(firebaseIdToken: string): Promise<PortalSessionData>;
  loadHomepage(signal?: AbortSignal): Promise<HomepageContentState>;
  loadGoogleTagManagerSettings(
    signal?: AbortSignal,
  ): Promise<GoogleTagManagerSettingsState>;
  loadHistory(
    options?: HomepageHistoryOptions,
    signal?: AbortSignal,
  ): Promise<HomepageRevisionHistory>;
  loadRevision(
    revisionId: string,
    signal?: AbortSignal,
  ): Promise<HomepageRevisionDetail>;
  saveDraft(
    csrfToken: string,
    expectedLockVersion: number,
    content: HomepageContent,
  ): Promise<HomepageContentState>;
  publishRevision(
    csrfToken: string,
    expectedLockVersion: number,
    revisionId: string,
  ): Promise<HomepageContentState>;
  updateGoogleTagManagerSettings(
    csrfToken: string,
    expectedLockVersion: number,
    enabled: boolean,
    containerId: string | null,
    confirmation: GoogleTagManagerConfirmation,
  ): Promise<GoogleTagManagerSettingsState>;
  restoreRevision(
    csrfToken: string,
    expectedLockVersion: number,
    revisionId: string,
  ): Promise<HomepageContentState>;
  exportBackup(revisionId: string): Promise<HomepageBackupExport>;
}

const allowedRoles = new Set<Role>(roles);
const allowedPermissions = new Set<Permission>(permissions);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeRevisionId(revisionId: string): string {
  if (!/^[A-Za-z0-9_-]{1,128}$/u.test(revisionId)) {
    throw new HomepageEditorApiError(
      "The revision identifier is invalid.",
      400,
    );
  }
  return encodeURIComponent(revisionId);
}

async function responseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    throw new HomepageEditorApiError(
      "The portal received an unreadable server response.",
      response.status,
    );
  }
}

function apiFailure(response: Response, body: unknown): HomepageEditorApiError {
  const errorEnvelope = isRecord(body) && body.ok === false ? body : null;
  const errors =
    errorEnvelope && Array.isArray(errorEnvelope.errors)
      ? errorEnvelope.errors.filter(isRecord)
      : [];
  const message =
    errors.find((error) => typeof error.message === "string")?.message ??
    "The homepage request could not be completed.";
  const codes = errors
    .map((error) => error.code)
    .filter((code): code is string => typeof code === "string");
  const correlationId =
    errorEnvelope && typeof errorEnvelope.correlationId === "string"
      ? errorEnvelope.correlationId
      : response.headers.get("x-correlation-id");
  return new HomepageEditorApiError(
    message as string,
    response.status,
    codes,
    correlationId,
  );
}

async function envelopeData<T>(response: Response): Promise<T> {
  const body = await responseJson(response);
  if (!response.ok) throw apiFailure(response, body);
  if (!isRecord(body) || body.ok !== true || !("data" in body)) {
    throw new HomepageEditorApiError(
      "The portal received an invalid server response.",
      response.status,
    );
  }
  return (body as unknown as SuccessEnvelope<T>).data;
}

function requestHeaders(additional?: HeadersInit): Headers {
  const headers = new Headers(additional);
  headers.set("Accept", "application/json");
  return headers;
}

function mutationHeaders(csrfToken: string): Headers {
  return requestHeaders({
    "Content-Type": "application/json",
    "X-CSRF-Token": csrfToken,
  });
}

function firebaseSessionHeaders(firebaseIdToken: string): Headers {
  if (
    firebaseIdToken.length > 16_384 ||
    !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/u.test(firebaseIdToken)
  ) {
    throw new HomepageEditorApiError(
      "The verified identity token is invalid.",
      400,
    );
  }
  return requestHeaders({ Authorization: `Bearer ${firebaseIdToken}` });
}

function isBootstrapEligible(error: unknown): boolean {
  return (
    error instanceof HomepageEditorApiError &&
    error.codes.length === 1 &&
    error.codes[0] === "AUTHORIZATION_DENIED"
  );
}

function parsePortalSession(data: unknown): PortalSessionData {
  if (
    !isRecord(data) ||
    data.authenticated !== true ||
    !isRecord(data.subject)
  ) {
    throw new HomepageEditorApiError(
      "The portal received an invalid session response.",
      502,
    );
  }
  const subject = data.subject;
  const role = subject.role;
  const permissions = Array.isArray(subject.permissions)
    ? subject.permissions.filter(
        (permission): permission is Permission =>
          typeof permission === "string" &&
          allowedPermissions.has(permission as Permission),
      )
    : [];
  if (
    typeof subject.userId !== "string" ||
    typeof subject.organizationId !== "string" ||
    typeof role !== "string" ||
    !allowedRoles.has(role as Role) ||
    !isRecord(data.csrf) ||
    typeof data.csrf.token !== "string" ||
    typeof data.csrf.expiresAt !== "string" ||
    !isRecord(data.session) ||
    typeof data.session.idleExpiresAt !== "string" ||
    typeof data.session.absoluteExpiresAt !== "string"
  ) {
    throw new HomepageEditorApiError(
      "The portal received an invalid session response.",
      502,
    );
  }
  return {
    subject: {
      userId: subject.userId,
      roles: [role as Role],
      permissions,
      locationIds: [],
    },
    organizationId: subject.organizationId,
    csrfToken: data.csrf.token,
    csrfExpiresAt: data.csrf.expiresAt,
    idleExpiresAt: data.session.idleExpiresAt,
    absoluteExpiresAt: data.session.absoluteExpiresAt,
  };
}

function normalizedApiUrl(apiUrl: string): string {
  return apiUrl.replace(/\/+$/u, "");
}

export function createHomepageEditorApiClient(
  apiUrl: string,
  fetcher: HomepageEditorFetch = fetch,
): HomepageEditorApiClient {
  const baseUrl = normalizedApiUrl(apiUrl);
  const request = (path: string, init: RequestInit = {}) =>
    fetcher(`${baseUrl}${path}`, {
      ...init,
      credentials: "include",
      headers: requestHeaders(init.headers),
    });
  const mutate = <T>(
    path: string,
    csrfToken: string,
    body: Readonly<Record<string, unknown>>,
  ) =>
    request(path, {
      method: "POST",
      headers: mutationHeaders(csrfToken),
      body: JSON.stringify(body),
    }).then(envelopeData<T>);

  return {
    apiUrl: baseUrl,
    async loadSession(signal) {
      const data = await request("/v1/auth/session", {
        signal: signal ?? null,
      }).then(envelopeData<unknown>);
      return parsePortalSession(data);
    },
    async exchangeFirebaseSession(firebaseIdToken) {
      const init: RequestInit = {
        method: "POST",
        headers: firebaseSessionHeaders(firebaseIdToken),
        body: null,
      };
      try {
        const data = await request("/v1/auth/session", init).then(
          envelopeData<unknown>,
        );
        return parsePortalSession(data);
      } catch (error) {
        if (!isBootstrapEligible(error)) throw error;
        const data = await request("/v1/auth/owner-bootstrap", init).then(
          envelopeData<unknown>,
        );
        return parsePortalSession(data);
      }
    },
    loadHomepage(signal) {
      return request("/v1/admin/content/homepage", {
        signal: signal ?? null,
      }).then(envelopeData<HomepageContentState>);
    },
    loadGoogleTagManagerSettings(signal) {
      return request("/v1/admin/settings/google-tag-manager", {
        signal: signal ?? null,
      }).then(envelopeData<GoogleTagManagerSettingsState>);
    },
    loadHistory(options = {}, signal) {
      const query = new URLSearchParams();
      query.set("limit", String(options.limit ?? 50));
      if (options.beforeRevisionNumber !== undefined) {
        query.set("beforeRevisionNumber", String(options.beforeRevisionNumber));
      }
      return request(
        `/v1/admin/content/homepage/revisions?${query.toString()}`,
        { signal: signal ?? null },
      ).then(envelopeData<HomepageRevisionHistory>);
    },
    loadRevision(revisionId, signal) {
      return request(`/v1/preview/homepage/${safeRevisionId(revisionId)}`, {
        signal: signal ?? null,
      }).then(envelopeData<HomepageRevisionDetail>);
    },
    saveDraft(csrfToken, expectedLockVersion, content) {
      return mutate<HomepageContentState>(
        "/v1/admin/content/homepage/revisions",
        csrfToken,
        { expectedLockVersion, content },
      );
    },
    publishRevision(csrfToken, expectedLockVersion, revisionId) {
      return mutate<HomepageContentState>(
        "/v1/admin/content/homepage/publish",
        csrfToken,
        {
          expectedLockVersion,
          revisionId,
          confirmation: "PUBLISH",
        },
      );
    },
    updateGoogleTagManagerSettings(
      csrfToken,
      expectedLockVersion,
      enabled,
      containerId,
      confirmation,
    ) {
      return mutate<GoogleTagManagerSettingsState>(
        "/v1/admin/settings/google-tag-manager",
        csrfToken,
        {
          expectedLockVersion,
          enabled,
          containerId,
          confirmation,
        },
      );
    },
    restoreRevision(csrfToken, expectedLockVersion, revisionId) {
      return mutate<HomepageContentState>(
        `/v1/admin/content/homepage/revisions/${safeRevisionId(revisionId)}/restore`,
        csrfToken,
        { expectedLockVersion, confirmation: "RESTORE" },
      );
    },
    async exportBackup(revisionId) {
      const response = await request(
        `/v1/admin/content/homepage/revisions/${safeRevisionId(revisionId)}/export`,
      );
      const body = await responseJson(response);
      if (!response.ok) throw apiFailure(response, body);
      if (!isRecord(body) || body.format !== "whitehaven-homepage-backup") {
        throw new HomepageEditorApiError(
          "The portal received an invalid backup document.",
          response.status,
        );
      }
      return body as unknown as HomepageBackupExport;
    },
  };
}

export type HomepageEditorErrorEnvelope = ErrorEnvelope;
